import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
    knowledgeApi,
    KnowledgeAudience,
    RankedKnowledgeChunk,
} from '../../../api/knowledgeApi';

interface Props {
    professionOptions: { key: string; label: string }[];
}

const SUPPORTED_LANGUAGES = ['EN', 'NL', 'PL', 'RO', 'DE', 'IT', 'ES', 'FR', 'HU'];

/**
 * Map cosine distance (0 = identical, 1 = unrelated) to a friendly percentage
 * match + chip colour. Admins don't care about the underlying metric — they
 * just want "how good is this chunk?".
 */
function matchQuality(distance: number): {
    percent: number;
    color: 'success' | 'info' | 'warning' | 'default';
} {
    const percent = Math.max(0, Math.min(100, Math.round((1 - distance) * 100)));
    let color: 'success' | 'info' | 'warning' | 'default';
    if (percent >= 70) color = 'success';
    else if (percent >= 50) color = 'info';
    else if (percent >= 30) color = 'warning';
    else color = 'default';
    return { percent, color };
}

/**
 * Admin QA box — two modes:
 *   1. "Show chunks" — raw retrieval (same filters the live RAG path uses).
 *   2. "Interpret with AI" — same retrieval + sends the chunks to optima-ai
 *      to generate the worker-facing reply the bot would actually send.
 */
export const KnowledgeSearchPreview: React.FC<Props> = ({ professionOptions }) => {
    const [query, setQuery] = useState('');
    const [audience, setAudience] = useState<KnowledgeAudience>('WORKER');
    const [professionKeys, setProfessionKeys] = useState<string[]>([]);
    const [languages, setLanguages] = useState<string[]>([]);
    const [aiLanguage, setAiLanguage] = useState<string>('EN');
    const [topK, setTopK] = useState<number>(8);
    const [running, setRunning] = useState<null | 'chunks' | 'ai'>(null);
    const [results, setResults] = useState<RankedKnowledgeChunk[] | null>(null);
    const [aiReply, setAiReply] = useState<string | null>(null);
    const [aiUsedChunks, setAiUsedChunks] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const validate = (): boolean => {
        setError(null);
        if (!query.trim()) {
            setError('Type a query to preview retrieval.');
            return false;
        }
        return true;
    };

    const runChunks = async () => {
        if (!validate()) return;
        setRunning('chunks');
        setAiReply(null);
        setAiUsedChunks(null);
        try {
            const resp = await knowledgeApi.searchPreview({
                query: query.trim(),
                audience,
                professionKeys: professionKeys.length ? professionKeys : undefined,
                languages: languages.length ? languages : undefined,
                topK,
            });
            setResults(resp.chunks);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Search failed');
        } finally {
            setRunning(null);
        }
    };

    const runAi = async () => {
        if (!validate()) return;
        setRunning('ai');
        try {
            const resp = await knowledgeApi.answerPreview({
                query: query.trim(),
                audience,
                professionKeys: professionKeys.length ? professionKeys : undefined,
                languages: languages.length ? languages : undefined,
                topK,
                language: aiLanguage,
            });
            setResults(resp.chunks);
            setAiReply(resp.reply);
            setAiUsedChunks(resp.usedChunks);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'AI interpretation failed');
        } finally {
            setRunning(null);
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Retrieval preview
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Show the raw chunks the bot would retrieve, or send them to the AI to get the synthesised reply the worker would see.
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 1.5 }} alignItems={{ md: 'center' }}>
                <TextField
                    label="Query"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    size="small"
                    fullWidth
                    onKeyDown={(e) => { if (e.key === 'Enter' && running === null) runAi(); }}
                    placeholder='e.g. "When do workers get paid?"'
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="kb-prev-aud-label">Audience</InputLabel>
                    <Select
                        labelId="kb-prev-aud-label"
                        label="Audience"
                        value={audience}
                        onChange={(e) => setAudience(e.target.value as KnowledgeAudience)}
                    >
                        <MenuItem value="WORKER">WORKER</MenuItem>
                        <MenuItem value="ADMIN">ADMIN</MenuItem>
                        <MenuItem value="BOTH">BOTH</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel id="kb-prev-topk-label">Top K</InputLabel>
                    <Select
                        labelId="kb-prev-topk-label"
                        label="Top K"
                        value={topK}
                        onChange={(e) => setTopK(Number(e.target.value))}
                    >
                        {[3, 5, 8, 12, 20].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel id="kb-prev-ailang-label">AI reply lang</InputLabel>
                    <Select
                        labelId="kb-prev-ailang-label"
                        label="AI reply lang"
                        value={aiLanguage}
                        onChange={(e) => setAiLanguage(String(e.target.value))}
                    >
                        {SUPPORTED_LANGUAGES.map((l) => (
                            <MenuItem key={l} value={l}>{l}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
                    <InputLabel id="kb-prev-prof-label">Profession filter (optional)</InputLabel>
                    <Select
                        labelId="kb-prev-prof-label"
                        label="Profession filter (optional)"
                        multiple
                        value={professionKeys}
                        onChange={(e) => setProfessionKeys(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        renderValue={(selected) => (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {selected.map((p) => <Chip key={p} size="small" label={p} />)}
                            </Stack>
                        )}
                    >
                        {professionOptions.map((p) => (
                            <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
                    <InputLabel id="kb-prev-lang-label">Language filter (optional)</InputLabel>
                    <Select
                        labelId="kb-prev-lang-label"
                        label="Language filter (optional)"
                        multiple
                        value={languages}
                        onChange={(e) => setLanguages(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        renderValue={(selected) => (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {selected.map((l) => <Chip key={l} size="small" label={l} />)}
                            </Stack>
                        )}
                    >
                        {SUPPORTED_LANGUAGES.map((l) => (
                            <MenuItem key={l} value={l}>{l}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <Button
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={runChunks}
                    disabled={running !== null}
                >
                    {running === 'chunks' ? 'Loading…' : 'Show chunks'}
                </Button>
                <Button
                    variant="contained"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={runAi}
                    disabled={running !== null}
                >
                    {running === 'ai' ? 'Asking AI…' : 'Interpret with AI'}
                </Button>
            </Stack>

            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5 }}>{error}</Alert>}

            {running !== null && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {running === null && aiReply !== null && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'action.hover' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <AutoAwesomeIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            AI reply ({aiLanguage}) — used {aiUsedChunks ?? 0} chunk(s)
                        </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {aiReply || '(empty reply)'}
                    </Typography>
                </Paper>
            )}

            {running === null && results !== null && (
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {results.length} chunk(s) returned, ordered best match first.
                    </Typography>
                    {results.length === 0 && (
                        <Alert severity="info">No chunks matched. Try widening the scope filters or rephrasing the query.</Alert>
                    )}
                    <Stack spacing={1.5}>
                        {results.map((c, i) => {
                            const quality = matchQuality(c.distance);
                            return (
                                <Paper key={c.chunkId} variant="outlined" sx={{ p: 1.5 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                        <Chip size="small" label={`#${i + 1}`} />
                                        <Tooltip title="How closely this chunk matches your query">
                                            <Chip size="small" label={`${quality.percent}% match`} color={quality.color} />
                                        </Tooltip>
                                        <Typography variant="caption" color="text.secondary">{c.entryTitle}</Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {c.text}
                                    </Typography>
                                </Paper>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Paper>
    );
};
